import React, { useState, useEffect } from 'react';
import {
  bool, func, string, number, arrayOf, oneOfType
} from 'prop-types';
import {
  Modal,
  ModalHeader,
  Stepper,
} from '@one-thd/sui-atomic-components';
import { CabinetShape, SegmentShape } from './type';
import { Wall } from './Wall';
import { getPxInInches } from '../../../../utils/wall-size-utils';
import { useViewportResize } from '../hooks/cabinetVisualizerHooks';
import { CabinetInfoSection } from './CabinetInfoSection';
import { useCabinetVisualizerDigitalDataStore } from '../../../../store';

const DEFAULT_WIDTH = 144;
const scaleFactor = 100;

const DesignZoomModal = ({
  open,
  handleClose,
  height,
  width,
  depth,
  cabinets, rows, columns, segments,
  onRender,
  designId
}) => {
  const [scale, setScale] = useState(scaleFactor);
  const { trackZoomInteractionEvent } = useCabinetVisualizerDigitalDataStore();
  useEffect(() => {
    if (open) {
      trackZoomInteractionEvent();
    }
  }, [open]);

  const onClose = (event) => {
    setScale(scaleFactor);
    handleClose(event);
  };

  const onQuantityChange = (count) => {
    setScale(count);
  };

  let finalZoom = (scale / scaleFactor);

  const { width: viewPortWidth, setElementRef } = useViewportResize(finalZoom);

  // A parent component can tell the wall how to render. Useful for making mobile bigger
  if (onRender) {
    const newZoom = onRender(viewPortWidth);

    if (newZoom) {
      finalZoom = newZoom;
    }
  }

  return (
    <Modal
      size="lg"
      open={open}
      onClose={onClose}
      data-component="wall-designer-modal"
    >
      <div className="modal--container">
        <ModalHeader onClose={onClose}>
          <div className="sui-block sui-text-center">Zoom View</div>
        </ModalHeader>
        <div
          className="zoom-control sui-float-right sui-absolute sui-right-3 sui-my-3 sui-z-10"
        >
          <Stepper
            style={{ justifyContent: 'center' }}
            min={50}
            max={200}
            step={25}
            value={scale}
            defaultValue={100}
            onChange={onQuantityChange}
          />
        </div>
        <div
          className="modal--body viewport"
          ref={setElementRef}
          style={{
            display: 'flex',
            overflow: 'auto'
          }}
        >
          <div
            className="modal--stack viewport-content"
            style={{
              paddingTop: '0px',
              display: 'inline-flex',
              width: 'auto',
              height: 'auto'
            }}
          >
            <Wall
              cabinets={cabinets}
              segments={segments}
              rows={rows}
              columns={columns}
              pxInInches={getPxInInches(viewPortWidth, DEFAULT_WIDTH)}
              zoom={finalZoom}
              disableTouchPoints
              designId={designId}
            />
          </div>
        </div>
        <CabinetInfoSection height={height} width={width} depth={depth} />
      </div>
    </Modal>
  );
};

DesignZoomModal.propTypes = {
  open: bool,
  handleClose: func,
  height: oneOfType([string, number]),
  width: oneOfType([string, number]),
  depth: oneOfType([string, number]),
  cabinets: arrayOf(CabinetShape).isRequired,
  rows: number,
  columns: number,
  segments: arrayOf(SegmentShape).isRequired,
  onRender: func,
  designId: string
};

DesignZoomModal.defaultProps = {
  open: false,
  handleClose: func,
  height: 0,
  width: 0,
  depth: '',
  rows: 0,
  columns: 0,
  onRender: undefined,
  designId: ''
};

export default DesignZoomModal;
